// IE Hub - Dependents Service
// Handles all dependent DOB-related API operations with proper error handling and monitoring

import { httpDelete, httpGet, httpPatch, httpPost, httpPut } from '../http';
import { dependentsEndpoints } from '../endpoints';
import { femaCapture } from '../../fema/capture';
import type { RequestConfig, ServiceResponse } from '../types';

/**
 * Normalize API-provided child dependant number to a canonical integer-string.
 *
 * Legacy/DB-sourced records can come back as "1.0", "2.0", etc. We canonicalize those
 * to "1", "2" so form field names like `under16DependentsDOB_1` line up with
 * dynamic field generation (1-based indices).
 *
 * If the value is missing/invalid, we return undefined and the caller can decide how to handle it.
 */
const normalizeChildDependantNo = (raw: unknown): string | undefined => {
  if (raw === null || raw === undefined) return undefined;

  if (typeof raw === 'string' && raw.trim() === '') return undefined;

  const n =
    typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.trim()) : Number.NaN;

  if (!Number.isFinite(n)) return undefined;

  // Accept only integer-ish values (e.g., 1, 1.0, 2.00). Reject anything else defensively.
  const rounded = Math.round(n);
  if (Math.abs(n - rounded) > 1e-9) return undefined;

  // Indices are 1-based in the UI.
  if (rounded < 1) return undefined;

  return String(rounded);
};

/**
 * Type definition for Dependent DOB record
 */
export interface DependentDobRecord {
  creationDate?: string;
  lastUpdateDate?: string;
  createdBy?: number;
  lastUpdatedBy?: number;
  id?: number;
  customerId: number;
  userId?: number;
  childDependantDob?: string;
  childDependantDobStr?: string;
  childDependantNo?: string;
  dependantCategory?: string;
  ieEncryptionString?: string;
}

/**
 * Type definition for creating a new dependent
 */
export interface CreateDependentRequest {
  customerId: number;
  userId?: number;
  childDependantDob?: string;
  childDependantDobStr?: string;
  childDependantNo?: string;
  dependantCategory?: string;
  ieEncryptionString?: string;
  createdBy?: number;
}

/**
 * Type definition for updating a dependent
 */
export interface UpdateDependentRequest extends DependentDobRecord {
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
}

/**
 * Type definition for partial update of a dependent
 */
export interface PatchDependentRequest {
  id: number;
  customerId: number;
  lastUpdatedBy: number;
  lastUpdateDate: string;
  [key: string]: any; // Allow partial updates
}

/**
 * Dependents Service Class
 * Handles all dependent DOB-related API operations with proper error handling, retry logic, and monitoring
 */
export class DependentsService {
  // Cache for performance optimization
  private static dependentsCache = new Map<string, { data: DependentDobRecord[]; timestamp: number }>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all dependents for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getDependents(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<DependentDobRecord[]> {
    try {
      // Check cache first
      const cacheKey = customerId.toString();
      const cached = this.dependentsCache.get(cacheKey);
      const now = Date.now();

      if (cached && now - cached.timestamp < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        dependentsEndpoints.getDependents(customerId),
        config
      );

      // Handle both envelope and raw array formats
      const payload = (response as any)?.data;
      const dataArray: DependentDobRecord[] | null = Array.isArray(payload)
        ? (payload as DependentDobRecord[])
        : (payload?.data ?? null);

      if (Array.isArray(dataArray)) {
        // Canonicalize legacy values like "1.0" -> "1" so dynamic DOB fields bind correctly.
        const normalizedArray: DependentDobRecord[] = dataArray.map((dep) => {
          const normalizedNo = normalizeChildDependantNo(dep.childDependantNo);
          return normalizedNo ? { ...dep, childDependantNo: normalizedNo } : dep;
        });

        this.dependentsCache.set(cacheKey, {
          data: normalizedArray,
          timestamp: now,
        });
        return {
          success: true,
          data: normalizedArray,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error('Failed to fetch dependents data');
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dependents_get_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Create a new dependent DOB record
   * @param createData - The dependent data to create
   * @param config - Optional request configuration
   */
  static async createDependent(
    createData: CreateDependentRequest,
    config?: RequestConfig
  ): ServiceResponse<DependentDobRecord> {
    try {
      // Validate mandatory fields
      if (!createData.customerId) {
        throw new Error('Missing mandatory field: customerId');
      }

      const response = await httpPost<any>(
        dependentsEndpoints.createDependent,
        createData,
        config
      );

      const createdData: DependentDobRecord | undefined = response?.data?.data || response?.data;
      if (createdData) {
        // Clear cache for this customer
        this.dependentsCache.delete(createData.customerId.toString());

        return {
          success: true,
          data: createdData,
          message: 'Dependent created successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error('Failed to create dependent');
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dependents_create_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId: createData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Update a dependent DOB record
   * @param updateData - The dependent data to update
   * @param config - Optional request configuration
   */
  static async updateDependent(
    updateData: UpdateDependentRequest,
    config?: RequestConfig
  ): ServiceResponse<DependentDobRecord> {
    try {
      // Validate mandatory fields
      if (
        !updateData.id ||
        !updateData.customerId ||
        !updateData.lastUpdatedBy ||
        !updateData.lastUpdateDate
      ) {
        throw new Error(
          'Missing mandatory fields: id, customerId, lastUpdatedBy, lastUpdateDate'
        );
      }

      const response = await httpPut<any>(
        dependentsEndpoints.updateDependent,
        updateData,
        config
      );

      const updatedData: DependentDobRecord | undefined = response?.data?.data || response?.data;
      if (updatedData) {
        // Clear cache for this customer
        this.dependentsCache.delete(updateData.customerId.toString());

        return {
          success: true,
          data: updatedData,
          message: 'Dependent updated successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error('Failed to update dependent');
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dependents_update_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId: updateData.customerId, dependentId: updateData.id },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Partially update a dependent DOB record
   * @param id - The dependent record ID
   * @param patchData - The partial dependent data to update
   * @param config - Optional request configuration
   */
  static async patchDependent(
    id: string | number,
    patchData: PatchDependentRequest,
    config?: RequestConfig
  ): ServiceResponse<DependentDobRecord> {
    try {
      // Validate mandatory fields
      if (
        !patchData.customerId ||
        !patchData.lastUpdatedBy ||
        !patchData.lastUpdateDate
      ) {
        throw new Error(
          'Missing mandatory fields: customerId, lastUpdatedBy, lastUpdateDate'
        );
      }

      const response = await httpPatch<any>(
        dependentsEndpoints.patchDependent(id),
        patchData,
        config
      );

      const patchedData: DependentDobRecord | undefined = response?.data?.data || response?.data;
      if (patchedData) {
        // Clear cache for this customer
        this.dependentsCache.delete(patchData.customerId.toString());

        return {
          success: true,
          data: patchedData,
          message: 'Dependent partially updated successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error('Failed to partially update dependent');
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dependents_patch_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId: patchData.customerId, dependentId: id },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Delete a dependent DOB record
   * @param customerId - The customer ID
   * @param dependentId - The dependent record ID to delete
   * @param config - Optional request configuration
   */
  static async deleteDependent(
    customerId: string | number,
    dependentId: string | number,
    config?: RequestConfig
  ): ServiceResponse<void> {
    try {
      await httpDelete<any>(
        dependentsEndpoints.deleteDependent(dependentId, customerId),
        config
      );

      // Clear cache for this customer
      this.dependentsCache.delete(customerId.toString());

      return {
        success: true,
        data: undefined,
        message: 'Dependent deleted successfully',
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture('dependents_delete_error', error, {
          category: 'api',
          severity: 'error',
          context: { customerId, dependentId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Clear cache for a specific customer
   * @param customerId - The customer ID to clear cache for
   */
  static clearCustomerCache(customerId: string | number): void {
    this.dependentsCache.delete(customerId.toString());
  }

  /**
   * Clear all caches
   */
  static clearAllCaches(): void {
    this.dependentsCache.clear();
  }

  /**
   * Get cached dependents data if available
   * @param customerId - The customer ID
   */
  static getCachedDependents(
    customerId: string | number
  ): DependentDobRecord[] | null {
    const cacheKey = customerId.toString();
    const cached = this.dependentsCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    return null;
  }
}

export default DependentsService;
